<template>
  <div>
    <v-alert v-for="a in alerts" :key="`after-alert-${a.key}`" :type="a.type" dense outlined class="mb-3">{{ a.text }}</v-alert>

    <div v-if="actions.length" class="mb-3">
      <step-button
        v-for="a in actions"
        :key="`after-action-${a.key}`"
        :override="a"
        :label="a.label"
        :color="a.color || 'primary'"
        :outlined="!!a.outlined"
        :busy="busy"
        :loading="loadingKey === a.key"
        @click="onAction(a)"
      />
    </div>

    <div v-if="verifying && verifyProgress" class="mb-3">
      <v-progress-linear :value="((verifyProgress.index - 1) / verifyProgress.total) * 100" height="18" />
      <div class="mt-1">{{ verifyProgressText }}</div>
    </div>

    <kick-run-view v-if="restoredRun" :run="restoredRun" :loop="kickLoop" :busy="kickBusy" :note="kickNote" :poi-state="preflight.poiState" />

    <v-alert v-if="result" :type="result.type" dense outlined class="mb-3">
      {{ result.text }}
      <ul v-if="result.rows.length" class="mb-0 mt-1">
        <li v-for="(r, i) in result.rows" :key="`result-row-${i}`" :class="toneClass(r.tone)">
          <strong>{{ r.avcId }}</strong> {{ r.outcome }}<span v-if="r.message">: {{ r.message }}</span>
        </li>
      </ul>
    </v-alert>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mb-3">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
    <rollback-form-dialog
      v-model="formOpen"
      :dialog="formDialog"
      :ctx="ctx"
      :cvc-name="preflight.cvcName"
      :now="now"
      @proceed="submitForm"
    />
    <type-to-confirm v-model="bringUpOpen" v-bind="bringUpDlg" :cvc-name="preflight.cvcName" @proceed="bringUp" />
    <type-to-confirm v-model="restoreOpen" v-bind="restoreDlg" :cvc-name="preflight.cvcName" @proceed="restore" />
    <type-to-confirm v-model="moveBackOpen" v-bind="moveBackDlg" :cvc-name="preflight.cvcName" @proceed="moveBack" />
    <type-to-confirm v-model="closeOpen" v-bind="closeDlg" :cvc-name="preflight.cvcName" @proceed="close" />
    <type-to-confirm v-model="stopOpen" v-bind="stopDlg" :cvc-name="preflight.cvcName" @proceed="stop" />
    <type-to-confirm v-model="kickOpen" v-bind="kickDlg" :cvc-name="preflight.cvcName" @proceed="kickRestored" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { dialogProps } from "@/utils/cvcMigrationPage";
import {
  afterActions,
  afterAlerts,
  afterConfirm,
  afterDialog,
  afterResult,
  confirmBackRequest,
  restoredKickRun,
  startRequest,
  windowRequest,
} from "@/utils/cvcMigrationRollbackAfter";
import StepButton from "./step-button";
import TypeToConfirm from "./type-to-confirm";
import RollbackFormDialog from "./rollback-form-dialog";
import KickRunView from "./kick-run-view";
import SectionActions from "./section-actions";
import VerifyRun from "./verify-run";

const TONE_CLASS = {
  green: "green--text text--darken-2",
  amber: "orange--text text--darken-3",
  red: "red--text text--darken-2",
  grey: "grey--text text--darken-2",
};

// The buttons, dialogs and answers of a rollback after the re-point confirm: start,
// check and fix the source, bring it up, put the old IPs back, confirm the re-point back,
// move Usher back, close or stop. Everything it shows comes from `ctx` (the pure rules);
// every button is on only when the server says so, and every step is one store action.
export default {
  name: "CvcMigrationRollbackAfter",
  components: { StepButton, TypeToConfirm, RollbackFormDialog, KickRunView },
  mixins: [SectionActions, VerifyRun],
  // The page owns the kick run (it outlives this panel); this panel asks it to start a
  // run of the restored statics and shows the run when it is one.
  inject: ["cvcKickStart"],
  props: {
    ctx: { type: Object, required: true },
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    now: { type: Date, required: true },
    kickRun: { type: Object, default: null },
    kickLoop: { type: Boolean, default: false },
    kickBusy: { type: Boolean, default: false },
    kickNote: { type: String, default: "" },
  },
  data: () => ({
    formKind: "",
    formOpen: false,
    bringUpOpen: false,
    restoreOpen: false,
    moveBackOpen: false,
    closeOpen: false,
    stopOpen: false,
    kickOpen: false,
    result: null,
    // The actions whose last run in this session failed: their button reads as a retry.
    failedKeys: [],
  }),
  computed: {
    actions () {
      return afterActions(this.preflight.steps, this.ctx, this.failedKeys);
    },
    alerts () {
      return afterAlerts(this.ctx);
    },
    formDialog () {
      return this.formKind ? afterDialog(this.formKind, this.ctx) : null;
    },
    bringUpDlg () {
      return dialogProps(afterDialog("bringUp", this.ctx));
    },
    restoreDlg () {
      return dialogProps(afterDialog("restore", this.ctx));
    },
    moveBackDlg () {
      return dialogProps(afterDialog("moveBack", this.ctx));
    },
    closeDlg () {
      return dialogProps(afterDialog("close", this.ctx));
    },
    stopDlg () {
      return dialogProps(afterDialog("stop", this.ctx));
    },
    kickDlg () {
      return dialogProps(afterDialog("kickRestored", this.ctx));
    },
    restoredRun () {
      return restoredKickRun(this.kickRun);
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationStartRollback",
      "cvcMigrationRollbackWindow",
      "cvcMigrationStopRollback",
      "cvcMigrationConfirmRepointBack",
      "cvcMigrationMoveBack",
      "cvcMigrationRollbackSource",
      "cvcMigrationRestoreOldIps",
      "cvcMigrationBuildOnSource",
      "cvcMigrationRepolicyOnSource",
      "cvcMigrationRemoveFromSource",
    ]),
    toneClass (tone) {
      return TONE_CLASS[tone] || TONE_CLASS.grey;
    },
    onAction (a) {
      if (a.key === "startRollback") this.openForm("start");
      else if (a.key === "window") this.openForm("window");
      else if (a.key === "confirmBack") this.openForm("confirmBack");
      else if (a.key === "bringUp") this.bringUpOpen = true;
      else if (a.key === "restore") this.restoreOpen = true;
      else if (a.key === "moveBack") this.moveBackOpen = true;
      else if (a.key === "close") this.closeOpen = true;
      else if (a.key === "stop") this.stopOpen = true;
      else if (a.key === "verifySource") this.verifySource();
      else if (a.key === "kickRestored") this.kickOpen = true;
      else this.askFix(a.key);
    },
    openForm (kind) {
      this.formKind = kind;
      this.formOpen = true;
    },
    // Runs one step, shows its answer, and remembers whether it failed (for the retry label).
    async step (key, kind, call) {
      this.result = null;
      const ok = await this.run(key, async () => {
        const answer = await call();
        this.result = afterResult(kind, answer, this.ctx);
        return true;
      });
      this.failedKeys = ok === undefined ? this.failedKeys.filter(k => k !== key).concat(key) : this.failedKeys.filter(k => k !== key);
    },
    submitForm (form) {
      const kind = this.formKind;
      if (kind === "start") {
        this.step("start", kind, () => this.cvcMigrationStartRollback({ cvcId: this.cvcId, ...startRequest(form) }));
      } else if (kind === "window") {
        this.step("window", kind, () => this.cvcMigrationRollbackWindow({ cvcId: this.cvcId, ...windowRequest(form) }));
      } else if (kind === "confirmBack") {
        this.step("confirmBack", kind, () => this.cvcMigrationConfirmRepointBack({ cvcId: this.cvcId, ...confirmBackRequest(form) }));
      }
    },
    bringUp () {
      this.step("bringUp", "bringUp", () => this.cvcMigrationRollbackSource({ cvcId: this.cvcId }));
    },
    restore () {
      this.step("restore", "restore", () => this.cvcMigrationRestoreOldIps({ cvcId: this.cvcId }));
    },
    moveBack () {
      this.step("moveBack", "moveBack", () => this.cvcMigrationMoveBack({ cvcId: this.cvcId }));
    },
    stop () {
      this.step("stop", "stop", () => this.cvcMigrationStopRollback({ cvcId: this.cvcId }));
    },
    // Reads the source router like Verify target does, with the source host. The answer is
    // handed to the panel (the steps and the network lines read it), not to the page.
    verifySource () {
      this.run("verifySource", async () => {
        const out = await this.verifyRun(undefined, { id: this.preflight.sourceNetworkHostId, name: this.preflight.sourceHostname });
        if (out.results.length) this.$emit("source-verified", { answer: out.merged, at: new Date() });
      });
    },
    // A run over the statics that came back on their new IP: never a Kick all.
    kickRestored () {
      this.run("kickRestored", () => this.cvcKickStart({ ids: this.ctx.candidates, kickAll: false, restored: true }));
    },
    // Close, remove the target copies, release the IPs: the panel runs it (it keeps the
    // result and the retry of the release once the record is closed).
    close ({ checked }) {
      this.$emit("close-rolled-back", { checked });
    },
    // Build, repolicy and remove on the source list what they touch in a plain confirm.
    askFix (key) {
      const plan = afterConfirm(key, this.ctx);
      const calls = {
        buildSource: () => this.cvcMigrationBuildOnSource({ cvcId: this.cvcId, avcIds: plan.avcIds }),
        repolicySource: () => this.cvcMigrationRepolicyOnSource({ cvcId: this.cvcId, avcIds: plan.avcIds }),
        removeSource: () => this.cvcMigrationRemoveFromSource({ cvcId: this.cvcId, copyIds: plan.copyIds }),
      };
      if (calls[key]) this.ask(plan.text, plan.proceedText, () => this.step(key, key, calls[key]));
    },
  },
};
</script>
